import React, { useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { SimState } from '../hooks/useSimulation';

export const FLOCK_COLORS = ['#00e5ff', '#ff9800', '#69ff47', '#ea80fc'];
const ERASER_RADIUS = 50; // simulation units

export type InteractionMode = 'none' | 'addFish0' | 'addFish1' | 'addFish2' | 'addFood' | 'addPredator' | 'addWall' | 'eraser';

type Props = {
  stateRef: React.MutableRefObject<SimState | null>;
  mapWidth: number;
  mapHeight: number;
  mode: InteractionMode;
  onAddFish: (x: number, y: number, flock: number) => void;
  onAddFood: (x: number, y: number) => void;
  onAddPredator: (x: number, y: number) => void;
  onAddWall: (x1: number, y1: number, x2: number, y2: number) => void;
  onErase: (x: number, y: number, radius: number) => void;
};

// Draw a streamlined cyan fish with dorsal fin and eye
function drawCyanFish(ctx: CanvasRenderingContext2D, size: number, color: string) {
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(-size * 0.35, -size * 0.48);
  ctx.lineTo(-size * 0.75, 0);
  ctx.lineTo(-size * 0.35, size * 0.48);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  // Dorsal fin
  ctx.beginPath();
  ctx.moveTo(size * 0.1, -size * 0.46);
  ctx.lineTo(size * 0.38, -size * 0.82);
  ctx.lineTo(size * 0.62, -size * 0.46);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  // Eye
  ctx.beginPath();
  ctx.arc(size * 0.44, -size * 0.1, size * 0.14, 0, Math.PI * 2);
  ctx.fillStyle = 'white';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(size * 0.47, -size * 0.1, size * 0.08, 0, Math.PI * 2);
  ctx.fillStyle = '#001a33';
  ctx.fill();
}

// Draw a rounder orange fish with pectoral fin, different dorsal, eye
function drawOrangeFish(ctx: CanvasRenderingContext2D, size: number, color: string) {
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(-size * 0.18, -size * 0.6);
  ctx.lineTo(-size * 0.65, 0);
  ctx.lineTo(-size * 0.18, size * 0.6);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  // Pectoral fin (lower side)
  ctx.beginPath();
  ctx.moveTo(size * 0.18, size * 0.52);
  ctx.lineTo(-size * 0.12, size * 0.95);
  ctx.lineTo(-size * 0.45, size * 0.52);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  // Short rounded dorsal fin
  ctx.beginPath();
  ctx.moveTo(size * 0.05, -size * 0.58);
  ctx.lineTo(size * 0.32, -size * 0.88);
  ctx.lineTo(size * 0.55, -size * 0.58);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  // Eye
  ctx.beginPath();
  ctx.arc(size * 0.44, -size * 0.12, size * 0.14, 0, Math.PI * 2);
  ctx.fillStyle = 'white';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(size * 0.47, -size * 0.12, size * 0.08, 0, Math.PI * 2);
  ctx.fillStyle = '#001a33';
  ctx.fill();
}

// Draw an angular green fish with spiky double dorsal fin, eye
function drawGreenFish(ctx: CanvasRenderingContext2D, size: number, color: string) {
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(size * 0.38, -size * 0.32);
  ctx.lineTo(-size * 0.18, -size * 0.52);
  ctx.lineTo(-size * 0.72, 0);
  ctx.lineTo(-size * 0.18, size * 0.52);
  ctx.lineTo(size * 0.38, size * 0.32);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  // Spiky double dorsal fin
  ctx.beginPath();
  ctx.moveTo(size * 0.36, -size * 0.32);
  ctx.lineTo(size * 0.55, -size * 0.72);
  ctx.lineTo(size * 0.22, -size * 0.36);
  ctx.lineTo(size * 0.08, -size * 0.65);
  ctx.lineTo(-size * 0.08, -size * 0.44);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  // Pelvic fin (lower)
  ctx.beginPath();
  ctx.moveTo(size * 0.15, size * 0.48);
  ctx.lineTo(size * 0.0, size * 0.78);
  ctx.lineTo(-size * 0.22, size * 0.48);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();

  // Eye
  ctx.beginPath();
  ctx.arc(size * 0.48, -size * 0.1, size * 0.14, 0, Math.PI * 2);
  ctx.fillStyle = 'white';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(size * 0.51, -size * 0.1, size * 0.08, 0, Math.PI * 2);
  ctx.fillStyle = '#001a33';
  ctx.fill();
}

// Draw a shark-like predator with teeth, tall dorsal, animated pectoral fin, yellow eye
function drawPredator(ctx: CanvasRenderingContext2D, size: number, time: number) {
  const body = '#ff1744';
  const dark = '#b71c1c';

  // Main body
  ctx.beginPath();
  ctx.moveTo(size * 1.1, 0);
  ctx.lineTo(-size * 0.25, -size * 0.55);
  ctx.lineTo(-size * 0.85, -size * 0.18);
  ctx.lineTo(-size * 0.85, size * 0.18);
  ctx.lineTo(-size * 0.25, size * 0.55);
  ctx.closePath();
  ctx.fillStyle = body;
  ctx.fill();

  // Tail fin (forked)
  ctx.beginPath();
  ctx.moveTo(-size * 0.72, 0);
  ctx.lineTo(-size * 1.22, -size * 0.45);
  ctx.lineTo(-size * 0.85, 0);
  ctx.lineTo(-size * 1.22, size * 0.45);
  ctx.closePath();
  ctx.fillStyle = dark;
  ctx.fill();

  // Tall dorsal fin
  ctx.beginPath();
  ctx.moveTo(size * 0.12, -size * 0.52);
  ctx.lineTo(size * 0.48, -size * 1.12);
  ctx.lineTo(size * 0.78, -size * 0.52);
  ctx.closePath();
  ctx.fillStyle = dark;
  ctx.fill();

  // Pectoral fin (animated)
  const wave = Math.sin(time * 0.006) * 0.14;
  ctx.beginPath();
  ctx.moveTo(size * 0.08, size * 0.5);
  ctx.lineTo(-size * 0.22 + wave * size, size * 1.0);
  ctx.lineTo(-size * 0.52, size * 0.5);
  ctx.closePath();
  ctx.fillStyle = dark;
  ctx.fill();

  // Teeth (3 triangles at snout)
  ctx.fillStyle = 'white';
  for (let t = 0; t < 3; t++) {
    const tx = size * 1.1 - t * size * 0.2;
    ctx.beginPath();
    ctx.moveTo(tx, -size * 0.05);
    ctx.lineTo(tx + size * 0.14, -size * 0.24);
    ctx.lineTo(tx - size * 0.06, -size * 0.05);
    ctx.closePath();
    ctx.fill();
  }

  // Eye (yellow with black pupil)
  ctx.beginPath();
  ctx.arc(size * 0.62, -size * 0.18, size * 0.18, 0, Math.PI * 2);
  ctx.fillStyle = '#ffe600';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(size * 0.65, -size * 0.18, size * 0.1, 0, Math.PI * 2);
  ctx.fillStyle = '#000';
  ctx.fill();
}

const Board = ({ stateRef, mapWidth, mapHeight, mode, onAddFish, onAddFood, onAddPredator, onAddWall, onErase }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const flashRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const wallStartRef = useRef<{ x: number; y: number } | null>(null);
  const wallPreviewRef = useRef<{ x: number; y: number } | null>(null);
  const eraserPosRef = useRef<{ cx: number; cy: number } | null>(null);
  const isErasingRef = useRef(false);
  const lastEraseRef = useRef(0);
  const modeRef = useRef(mode);

  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Draw loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    const resize = () => {
      const parent = canvas.parentElement!;
      const scale = Math.min(parent.clientWidth / mapWidth, parent.clientHeight / mapHeight);
      canvas.width = Math.floor(mapWidth * scale);
      canvas.height = Math.floor(mapHeight * scale);
    };
    resize();
    const ro = new ResizeObserver(() => requestAnimationFrame(resize));
    ro.observe(canvas.parentElement!);

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      const sx = w / mapWidth;
      const sy = h / mapHeight;
      const state = stateRef.current;
      const now = Date.now();

      ctx.fillStyle = '#0a1628';
      ctx.fillRect(0, 0, w, h);

      // Grid
      ctx.strokeStyle = '#1a2a3a';
      ctx.lineWidth = 1;
      for (let i = 0; i <= w; i += 100 * sx) {
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, h); ctx.stroke();
      }
      for (let j = 0; j <= h; j += 100 * sy) {
        ctx.beginPath(); ctx.moveTo(0, j); ctx.lineTo(w, j); ctx.stroke();
      }

      if (!state) {
        animRef.current = requestAnimationFrame(draw);
        return;
      }

      // Walls
      ctx.strokeStyle = '#607d8b';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      for (const wall of state.walls) {
        ctx.beginPath();
        ctx.moveTo(wall[0] * sx, wall[1] * sy);
        ctx.lineTo(wall[2] * sx, wall[3] * sy);
        ctx.stroke();
      }

      // Food (pulsing green)
      const pulse = 0.6 + 0.4 * Math.sin(now / 300);
      for (const food of state.food) {
        ctx.globalAlpha = pulse;
        ctx.fillStyle = '#76ff03';
        ctx.beginPath();
        ctx.arc(food.x * sx, food.y * sy, 6, 0, 2 * Math.PI);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      // Boids
      for (const boid of state.boids) {
        const x = boid.x * sx;
        const y = boid.y * sy;
        const angle = Math.atan2(boid.vy, boid.vx);
        const isPred = boid.type === 'predator';

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle);

        if (isPred) {
          ctx.shadowColor = '#ff1744';
          ctx.shadowBlur = 16;
          drawPredator(ctx, 12, now);
        } else {
          const color = FLOCK_COLORS[boid.flock] ?? '#00e5ff';
          const size = 8;
          if (boid.flock === 0) drawCyanFish(ctx, size, color);
          else if (boid.flock === 1) drawOrangeFish(ctx, size, color);
          else drawGreenFish(ctx, size, color);
        }

        ctx.restore();
      }

      // Click flash
      if (flashRef.current) {
        const { x, y, t } = flashRef.current;
        const elapsed = now - t;
        if (elapsed < 400) {
          ctx.globalAlpha = 1 - elapsed / 400;
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(x, y, (elapsed / 400) * 28, 0, 2 * Math.PI);
          ctx.stroke();
          ctx.globalAlpha = 1;
        } else {
          flashRef.current = null;
        }
      }

      // Wall drawing preview
      if (wallStartRef.current && wallPreviewRef.current) {
        ctx.strokeStyle = 'rgba(255,255,255,0.5)';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(wallStartRef.current.x, wallStartRef.current.y);
        ctx.lineTo(wallPreviewRef.current.x, wallPreviewRef.current.y);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Eraser cursor
      if (modeRef.current === 'eraser' && eraserPosRef.current) {
        const { cx, cy } = eraserPosRef.current;
        const radiusPx = ERASER_RADIUS * sx;
        ctx.strokeStyle = isErasingRef.current ? 'rgba(255,80,80,0.9)' : 'rgba(255,80,80,0.5)';
        ctx.lineWidth = isErasingRef.current ? 2 : 1.5;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.arc(cx, cy, radiusPx, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.setLineDash([]);
        if (isErasingRef.current) {
          ctx.fillStyle = 'rgba(255,80,80,0.08)';
          ctx.beginPath();
          ctx.arc(cx, cy, radiusPx, 0, 2 * Math.PI);
          ctx.fill();
        }
      }

      // Faded border overlay (indicates wrap-around edges)
      const bw = Math.min(w, h) * 0.06;
      let grd = ctx.createLinearGradient(0, 0, bw, 0);
      grd.addColorStop(0, 'rgba(255,255,255,0.15)');
      grd.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grd; ctx.fillRect(0, 0, bw, h);

      grd = ctx.createLinearGradient(w - bw, 0, w, 0);
      grd.addColorStop(0, 'rgba(255,255,255,0)');
      grd.addColorStop(1, 'rgba(255,255,255,0.15)');
      ctx.fillStyle = grd; ctx.fillRect(w - bw, 0, bw, h);

      grd = ctx.createLinearGradient(0, 0, 0, bw);
      grd.addColorStop(0, 'rgba(255,255,255,0.15)');
      grd.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = grd; ctx.fillRect(0, 0, w, bw);

      grd = ctx.createLinearGradient(0, h - bw, 0, h);
      grd.addColorStop(0, 'rgba(255,255,255,0)');
      grd.addColorStop(1, 'rgba(255,255,255,0.15)');
      ctx.fillStyle = grd; ctx.fillRect(0, h - bw, w, bw);

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
    };
  }, [mapWidth, mapHeight, stateRef]);

  // Document-level mouse listeners (wall drawing + eraser)
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;

      if (modeRef.current === 'eraser') {
        eraserPosRef.current = { cx, cy };
        if (isErasingRef.current) {
          const now = Date.now();
          if (now - lastEraseRef.current > 80) {
            lastEraseRef.current = now;
            const x = (cx / canvas.width) * mapWidth;
            const y = (cy / canvas.height) * mapHeight;
            onErase(x, y, ERASER_RADIUS);
          }
        }
        return;
      }

      if (wallStartRef.current) {
        wallPreviewRef.current = { x: cx, y: cy };
      }
    };

    const onUp = (e: MouseEvent) => {
      isErasingRef.current = false;

      if (!wallStartRef.current || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const ex = e.clientX - rect.left;
      const ey = e.clientY - rect.top;
      const { x: sx, y: sy } = wallStartRef.current;
      const simX1 = (sx / canvas.width) * mapWidth;
      const simY1 = (sy / canvas.height) * mapHeight;
      const simX2 = (ex / canvas.width) * mapWidth;
      const simY2 = (ey / canvas.height) * mapHeight;
      const dx = simX2 - simX1;
      const dy = simY2 - simY1;
      if (Math.sqrt(dx * dx + dy * dy) > 15) {
        onAddWall(simX1, simY1, simX2, simY2);
      }
      wallStartRef.current = null;
      wallPreviewRef.current = null;
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [mapWidth, mapHeight, onAddWall, onErase]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || mode === 'none' || mode === 'addWall' || mode === 'eraser') return;
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const x = (cx / canvas.width) * mapWidth;
      const y = (cy / canvas.height) * mapHeight;
      flashRef.current = { x: cx, y: cy, t: Date.now() };
      if (mode === 'addFish0') onAddFish(x, y, 0);
      else if (mode === 'addFish1') onAddFish(x, y, 1);
      else if (mode === 'addFish2') onAddFish(x, y, 2);
      else if (mode === 'addFood') onAddFood(x, y);
      else if (mode === 'addPredator') onAddPredator(x, y);
    },
    [mode, mapWidth, mapHeight, onAddFish, onAddFood, onAddPredator]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;

      if (mode === 'eraser') {
        isErasingRef.current = true;
        eraserPosRef.current = { cx, cy };
        const x = (cx / canvas.width) * mapWidth;
        const y = (cy / canvas.height) * mapHeight;
        lastEraseRef.current = Date.now();
        onErase(x, y, ERASER_RADIUS);
        return;
      }

      if (mode === 'addWall') {
        wallStartRef.current = { x: cx, y: cy };
        wallPreviewRef.current = { x: cx, y: cy };
      }
    },
    [mode, mapWidth, mapHeight, onErase]
  );

  const getCursor = () => {
    if (mode === 'eraser') return 'none';
    if (mode === 'none') return 'default';
    return 'crosshair';
  };

  return (
    <Box sx={{
      width: '100%', height: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: '#0a1628',
    }}>
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        style={{ display: 'block', cursor: getCursor() }}
      />
    </Box>
  );
};

export default Board;
